import type { FC } from "react";
import { ServiceAccountGitAuthenticationCard } from "@/components/settings/organization/service-accounts/ServiceAccountGitAuthenticationCard";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { type ServiceAccountHeaders } from "@/api";
import { Button } from "@/components/flexkit/Button";
import IconCirclePlus from "central-icons/IconCirclePlus";
import { withGitpodSize } from "@/assets/icons/utils";

const IconCirclePlusWithGitpodSize = withGitpodSize(IconCirclePlus);

export type ServiceAccountGitAuthenticationsListProps = {
    onAddAuthentication: () => void;
    headers?: ServiceAccountHeaders;
};

export const ServiceAccountGitAuthenticationsList: FC<ServiceAccountGitAuthenticationsListProps> = ({
    onAddAuthentication,
    headers,
}) => {
    const { data: hostAuthenticationTokens, isPending: isPendingAuthTokens } = useListHostAuthenticationTokens({
        enabled: !!headers,
        headers,
    });

    const isEmpty = hostAuthenticationTokens?.tokens.length === 0;

    return (
        <div className="flex flex-col gap-2">
            {!isPendingAuthTokens && isEmpty && (
                <EmptyStateCard
                    title="Add authentication"
                    description="Add Git authentication so this Service Account can access repositories and create pull requests."
                    data-testid="service-account-git-authentications-empty-state"
                >
                    <Button
                        variant="primary"
                        LeadingIcon={IconCirclePlusWithGitpodSize}
                        onClick={onAddAuthentication}
                        data-testid="service-account-git-authentications-empty-state"
                        data-tracking-id="add-git-authentication"
                        disabled={!headers}
                    >
                        Add Git Authentication
                    </Button>
                </EmptyStateCard>
            )}

            {headers &&
                hostAuthenticationTokens?.tokens.map((token) => (
                    <ServiceAccountGitAuthenticationCard key={token.id} token={token} headers={headers} />
                ))}
        </div>
    );
};
